/**
 * "THIS ADDRESS BELONGS TO THE OTHER SURFACE." 09 §"two named surfaces", 04 I6.
 *
 * Exactly one context is active per session, and it is a property of the SESSION — never of the
 * user, never of the request. So an address from the other surface is not a bug to paper over by
 * switching silently: switching is an explicit action, it wipes every scoped slot, and doing it on
 * the user's behalf because they followed a bookmark would make the two surfaces simultaneous for
 * exactly as long as it took to render.
 *
 * The screen therefore STATES the situation, in Hebrew. **CM-014: the next step is no longer "switch
 * from the context bar", because there is no switch.** An account's context comes from
 * `app_user.account_kind` at authentication and cannot change (OA-0001 §2.1), so the honest sentence
 * is that this address belongs to a surface this account does not have — and, when the session has
 * not been read yet, that it has not been read yet.
 *
 * This is presentation. It decides nothing: the gate would refuse the request regardless, and the
 * refusal would arrive as a `DenyBody` and render through `<DenialNotice>`.
 */

import { AppShell } from '../shell/AppShell';
import { StateBlock } from './StateBlock';
import type { SessionContext } from '../api/contract';

const WHERE_YOU_ARE_HE: Readonly<Record<SessionContext, string>> = {
  PLATFORM: 'אתה נמצא בקונסולת הפלטפורמה',
  TENANT: 'אתה נמצא בסביבת המשרד',
};

const WHAT_THIS_IS_HE: Readonly<Record<SessionContext, string>> = {
  PLATFORM: 'הדף הזה שייך לקונסולת הפלטפורמה, שהיא סביבה נפרדת מסביבת המשרד.',
  TENANT: 'הדף הזה שייך לסביבת המשרד, שהיא סביבה נפרדת מקונסולת הפלטפורמה.',
};

export interface SurfaceNoticeProps {
  /** The screen's own title, so the user still knows which address they reached. */
  readonly titleHe: string;
  /** The surface this address belongs to. */
  readonly requires: SessionContext;
  /** The surface the session is actually in — `null` until the server has stated one. */
  readonly actual: SessionContext | null;
}

/** No session read yet. Distinct from being on the other surface, and it says so. */
const NO_SESSION_TITLE_HE = 'ההקשר של החיבור עדיין לא נקרא';
const NO_SESSION_BODY_HE =
  'הדף הזה נפתח לפני שהשרת מסר באיזו סביבה החיבור נמצא. הסביבה נקבעת בכניסה למערכת ואינה נקבעת מהכתובת.';

export function SurfaceNotice({ titleHe, requires, actual }: SurfaceNoticeProps): JSX.Element {
  return (
    <AppShell titleHe={titleHe}>
      <StateBlock
        titleHe={actual === null ? NO_SESSION_TITLE_HE : WHERE_YOU_ARE_HE[actual]}
        bodyHe={actual === null ? NO_SESSION_BODY_HE : WHAT_THIS_IS_HE[requires]}
        tone="unavailable"
        nextStep={
          <p>הסביבה נקבעת בכניסה למערכת לפי סוג החשבון, ואינה ניתנת להחלפה מתוך הדפדפן.</p>
        }
      />
    </AppShell>
  );
}
